import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  Stack,
  Avatar,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Business as BusinessIcon,
  FiberNew as NewBrandsIcon,
  RemoveCircleOutline as LeavingBrandsIcon,
} from "@mui/icons-material";
import { useExplorerStore } from "../stores/explorerStore";
import { useAuth } from "../contexts/AuthContext";
import DayRangePicker from "../components/DayRangePicker";
import { ExplorerHeader } from "./Explorer/ExplorerHeader";
import apiService from "../services/api";
import { getS3Url, ensureDirectS3Url } from "../utils/imageUtils";
import { STALE_TIME, GC_TIME, formatNumber, brandCategoryLabel, explorerPaginationLabel } from "./Explorer/utils";

interface BrandMoverDetail {
  _id: string;
  name: string;
  category: string | null;
  logoUrl: string | null;
  s3Key: string | null;
  mentionCount: number;
}

const PAGE_SIZE = 12;

const BrandMoversTable: React.FC<{
  brands: BrandMoverDetail[];
  isLoading: boolean;
  onBrandClick: (id: string) => void;
}> = ({ brands, isLoading, onBrandClick }) => {
  const { t } = useTranslation("explorer");
  const [page, setPage] = useState(0);

  if (isLoading) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("brand")}</TableCell>
              <TableCell align="right">{t("mentions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
                    <Skeleton width={120} />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Skeleton width={40} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (brands.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("noBrandsThisPeriod")}
        </Typography>
      </Paper>
    );
  }

  const paginated = brands.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("brand")}</TableCell>
            <TableCell align="right">{t("mentions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginated.map((brand) => {
            const avatarSrc = brand.s3Key
              ? ensureDirectS3Url(getS3Url(brand.s3Key))
              : (brand.logoUrl ?? undefined);
            const categoryLabel = brand.category
              ? brandCategoryLabel(brand.category) || brand.category
              : undefined;

            return (
              <TableRow
                key={brand._id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => onBrandClick(brand._id)}
              >
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={avatarSrc} sx={{ width: 32, height: 32 }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {brand.name}
                      </Typography>
                      {categoryLabel && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {categoryLabel}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {brand.mentionCount > 0 ? (
                    <Chip
                      label={formatNumber(brand.mentionCount)}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {brands.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={brands.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) =>
            explorerPaginationLabel(from, to, count)
          }
        />
      )}
    </TableContainer>
  );
};

const ExplorerBrandMovers: React.FC = () => {
  const { t } = useTranslation("explorer");
const navigate = useNavigate();
  const { getDateFilterMinStartDate, getDateFilterMaxEndDate, getExplorerSponsorCategoryFilter } = useAuth();
  const { startDate, endDate, setStartDate, setEndDate } = useExplorerStore();
  const currentMinDate = getDateFilterMinStartDate();
  const currentMaxDate = getDateFilterMaxEndDate();
  const lockedCategory = getExplorerSponsorCategoryFilter();

  const { data, isLoading, error } = useQuery({
    queryKey: ["brand-trends", startDate, endDate, "weekly", lockedCategory || "all"],
    queryFn: () =>
      apiService.getExplorerBrandTrends(
        startDate,
        endDate,
        "weekly",
        lockedCategory || undefined,
        undefined,
        true,
      ),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const [categoryFilter, setCategoryFilter] = useState(
    () => lockedCategory || "",
  );

  useEffect(() => {
    if (lockedCategory) {
      setCategoryFilter(lockedCategory);
    }
  }, [lockedCategory]);

  const periodTotals = data?.periodTotals as any;
  const allNewBrandDetails: BrandMoverDetail[] =
    periodTotals?.newBrandDetails ?? [];
  const allLeavingBrandDetails: BrandMoverDetail[] =
    periodTotals?.leavingBrandDetails ?? [];

  const availableCategories = Array.from(
    new Set(
      [...allNewBrandDetails, ...allLeavingBrandDetails]
        .map((b) => b.category)
        .filter((c): c is string => !!c)
    )
  ).sort((a, b) =>
    (brandCategoryLabel(a) || a).localeCompare(brandCategoryLabel(b) || b)
  );

  const filterByCategory = (brands: BrandMoverDetail[]) =>
    categoryFilter
      ? brands.filter(
          (b) => b.category?.toLowerCase() === categoryFilter.toLowerCase()
        )
      : brands;

  const newBrandDetails = filterByCategory(allNewBrandDetails);
  const leavingBrandDetails = filterByCategory(allLeavingBrandDetails);

  const handleBrandClick = (brandId: string) => {
    navigate(`/explorador/brands/${brandId}`);
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <ExplorerHeader
        onBack={() => navigate(-1)}
        title={t("brandMovementsTitle")}
        subtitle={t("brandMovementsSubtitle")}
      >
        {!lockedCategory && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel shrink>{t("category")}</InputLabel>
            <Select
              value={categoryFilter}
              label={t("category")}
              onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty
              renderValue={(selected) =>
                selected
                  ? brandCategoryLabel(selected) || selected
                  : t("allFeminine")
              }
            >
              <MenuItem value="">{t("allFeminine")}</MenuItem>
              {availableCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {brandCategoryLabel(cat) || cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <DayRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
          minDate={currentMinDate}
          maxDate={currentMaxDate}
        />
      </ExplorerHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error cargando los datos. Por favor intenta de nuevo.
        </Alert>
      )}

      {/* Two-column tables */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems="flex-start"
      >
        {/* Nuevas Marcas */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <NewBrandsIcon sx={{ color: "info.main", fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>
              {t("newBrands")}
            </Typography>
            {!isLoading && (
              <Chip label={newBrandDetails.length} size="small" color="info" />
            )}
          </Stack>
          <BrandMoversTable
            brands={newBrandDetails}
            isLoading={isLoading}
            onBrandClick={handleBrandClick}
          />
        </Box>

        {/* Marcas Salientes */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <LeavingBrandsIcon sx={{ color: "warning.main", fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>
              {t("leavingBrands")}
            </Typography>
            {!isLoading && (
              <Chip
                label={leavingBrandDetails.length}
                size="small"
                color="warning"
              />
            )}
          </Stack>
          <BrandMoversTable
            brands={leavingBrandDetails}
            isLoading={isLoading}
            onBrandClick={handleBrandClick}
          />
        </Box>
      </Stack>
    </Container>
  );
};

export default ExplorerBrandMovers;
